import axios from 'axios';

import { getUserData, QUERYAPI } from '../constants/global';
import type { AssignedEntityLevel } from '../interface/agent-assignment';
import type { IChildResultDetail, ResultDetailOutcome } from '../interface/child-agent';

// Standalone client for the Query API's per-level result-detail endpoints. Like
// the agent-assignment client it attaches the agent's core JWT + PartyId but has
// NO 401->logout interceptor — a result-detail failure must never sign the agent
// out or fire the global error toast (this is auxiliary, read-only view data).
const childResultApi = axios.create({ baseURL: QUERYAPI });

childResultApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// The query service occasionally returns JSON with a Content-Type that prevents
// axios from auto-parsing — coerce string bodies back to JSON.
childResultApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

// The assignment level → result controller path segment. The level enum says
// `LGA`, but the controller is `LocalGovernmentResult`.
const RESULT_PATH: Record<AssignedEntityLevel, string> = {
	PollingUnit: 'PollingUnitResult',
	Ward: 'WardResult',
	LGA: 'LocalGovernmentResult',
	State: 'StateResult',
};

/**
 * Fetch a single uploaded result's full detail by its Mongo id (`resultId`),
 * which the AgentAssignment/by-entity item already carries on `assignmentMeta`.
 * The body is unwrapped (DTO at the top level). Non-2xx responses resolve
 * (validateStatus) so the caller can classify (e.g. 404 = result vanished).
 */
export const getChildResultById = async (level: AssignedEntityLevel, resultId: string): Promise<ResultDetailOutcome> => {
	const segment = RESULT_PATH[level] ?? RESULT_PATH.PollingUnit;
	try {
		const res = await childResultApi.get<IChildResultDetail>(`/api/v1/i-elect/${segment}/get-by-id/${resultId}`, {
			validateStatus: () => true,
		});
		return { reachable: true, httpStatus: res.status, body: res.data ?? null };
	} catch {
		return { reachable: false };
	}
};
